import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { ContentCopy, Delete, Edit, InfoOutlined } from '@mui/icons-material';
// import { data, states } from './makeData';
import {getAllVM, updateVM, createVM, deleteVM, getAllUsers, getVMMqttCredentials} from '../../api/routes'
import { TableRowsLoader } from '../../utils/TableRowsLoader';

const VendingMachine = () => {

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState('');
  const [admins, setAdmins] = useState([])
  const [mqttCredentialsDialogOpen, setMqttCredentialsDialogOpen] = useState(false);
  const [generatedMqttCredentials, setGeneratedMqttCredentials] = useState(null);
  const [credentialsDialogTitle, setCredentialsDialogTitle] = useState('MQTT Credentials');
  const [credentialsDialogDescription, setCredentialsDialogDescription] = useState('Copy these once and configure the Pi/ESP devices for this VM.');
  const [fetchingMqttCredentialsForVm, setFetchingMqttCredentialsForVm] = useState('');
  const [copyMessage, setCopyMessage] = useState('');

  const normalizeBoolean = (value) => value === true || value === 'true';
  const normalizeSingleItemCount = (value, enabled) => {
    if (!normalizeBoolean(enabled)) return 0;
    const count = parseInt(value, 10);
    return [1, 2, 3, 4].includes(count) ? count : 1;
  };

  const updateVMResponseData = (data, users) =>{
    data.map((item, index) => {
      data[index]["status"] = item.status === 10 ? 'Online' : (item.status === 20? "Offline" :(item.status === 0? "New":"Not Working"));
      data[index]["created_at"] = (new Date(item.created_at)).toLocaleString();
      data[index]["updated_at"] = (new Date(item.updated_at)).toLocaleString();
      const admin_details = users.filter(item => data[index]["admin_assigned_id"] === item.guid)[0];
      data[index]["admin_name"] = admin_details.first_name+ " "+ admin_details.last_name;
      data[index]["has_coin_dispenser"] = Boolean(item.metadata && item.metadata.has_coin_dispenser);
      data[index]["single_item_vm"] = Boolean(item.metadata && item.metadata.single_item_vm);
      data[index]["single_item_vm_count"] = item.metadata && item.metadata.single_item_vm ? (item.metadata.single_item_vm_count || 1) : 0;
    })
    return data;
  }

  const getAdminsFromUsers = (data) =>{
    return data.filter(item => item.is_admin)
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const users = await getAllUsers();
      setAdmins(getAdminsFromUsers(users));
      const response = await getAllVM();
      console.log({vms: response});
      setTableData(updateVMResponseData(response, users));
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    console.log({admins});
  }, []);

  const handleChange = (event) => {
      setAdmin(event.target.value);
  };

  const copyToClipboard = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopyMessage(`${label} copied`);
    } catch (error) {
      console.log('copy failed', error);
      setCopyMessage(`Unable to copy ${label}`);
    }
  };

  const handleCloseCopyMessage = () => {
    setCopyMessage('');
  };

  const handleCreateNewRow = async (values) => {
    // values["items_dispensed"] = 0;
    // values["status"] = "New";
    // values["sales_amount"] = 0;
    // values["created_at"] = new Date().toLocaleString()
    // values["updated_at"] = new Date().toLocaleString()
    values["admin_assigned"] = admin;
    values["has_coin_dispenser"] = normalizeBoolean(values["has_coin_dispenser"]);
    values["single_item_vm"] = normalizeBoolean(values["single_item_vm"]);
    values["single_item_vm_count"] = normalizeSingleItemCount(values["single_item_vm_count"], values["single_item_vm"]);
    const createResult = await createVM(values);
    setAdmin("");
    setCredentialsDialogTitle('MQTT Credentials Generated');
    setCredentialsDialogDescription('Copy these once and configure the Pi/ESP devices for this VM.');
    if (createResult && createResult.mqtt_credentials) {
      setGeneratedMqttCredentials(createResult.mqtt_credentials);
      setMqttCredentialsDialogOpen(true);
    }
    await fetchData();
  };

  const handleOpenMqttCredentials = async (row) => {
    const vmId = row?.original?.guid;
    if (!vmId) {
      return;
    }

    setFetchingMqttCredentialsForVm(vmId);
    try {
      const result = await getVMMqttCredentials(vmId);
      if (!result || !result.mqtt_credentials) {
        setCopyMessage('MQTT credentials unavailable for this VM');
        return;
      }

      setGeneratedMqttCredentials(result.mqtt_credentials);
      setCredentialsDialogTitle(`MQTT Credentials - ${row?.original?.name || vmId}`);
      setCredentialsDialogDescription(
        result.warning
          ? `${result.warning} Update the ESP with this password.`
          : 'Use these credentials in ESP for this VM.'
      );
      setMqttCredentialsDialogOpen(true);
    } catch (error) {
      console.log('failed to fetch vm mqtt credentials', error);
      const errorDetail = error?.response?.data?.detail || 'Failed to fetch MQTT credentials';
      setCopyMessage(String(errorDetail));
    } finally {
      setFetchingMqttCredentialsForVm('');
    }
  };

  const handleSaveRowEdits = async ({ exitEditingMode, row, values }) => {
    if (!Object.keys(validationErrors).length) {
      tableData[row.index] = values;
      //send/receive api updates here, then refetch or update local table data for re-render
      setTableData([...tableData]);
      values["admin_assigned"] = admin;
      delete values["admin_name"];
      console.log({edited_values: values});
      const edit_payload = {
        number_of_slots: values.number_of_slots,
        name: values.name,
        has_coin_dispenser: normalizeBoolean(values.has_coin_dispenser),
        single_item_vm: normalizeBoolean(values.single_item_vm),
        single_item_vm_count: normalizeSingleItemCount(values.single_item_vm_count, values.single_item_vm),
      };
      if (values.admin_assigned) {
        edit_payload["admin_assigned"] = values.admin_assigned
      }
      await updateVM(values.guid, edit_payload);
      setAdmin("");
      fetchData();
      exitEditingMode(); //required to exit editing mode and close modal
    }
  };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };

  const handleDeleteRow = useCallback(
    async (row) => {
      if (
        // eslint-disable-next-line no-restricted-globals
        !confirm(`Are you sure you want to delete ?`)
      ) {
        return;
      }
      //send api delete request here, then refetch or update local table data for re-render
      console.log({row}, row["original"]["guid"]);
      tableData.splice(row.index, 1);
      await deleteVM(row["original"]["guid"]);
      await fetchData();
    },
    [tableData],
  );

  const getCommonEditTextFieldProps = useCallback(
    (cell) => {
      return {
        error: !!validationErrors[cell.id],
        helperText: validationErrors[cell.id],
        onBlur: (event) => {
          // const isValid =
          //   cell.column.id === 'email'
          //     ? validateEmail(event.target.value)
          //     : cell.column.id === 'age'
          //     ? validateAge(+event.target.value)
          //     : validateRequired(event.target.value);
          // if (!isValid) {
          //   //set validation error for cell if invalid
          //   setValidationErrors({
          //     ...validationErrors,
          //     [cell.id]: `${cell.column.columnDef.header} is required`,
          //   });
          // } else {
          //   //remove validation error for cell if valid
          //   delete validationErrors[cell.id];
          //   setValidationErrors({
          //     ...validationErrors,
          //   });
          // }
        },
      };
    },
    [validationErrors],
  );

  // const getMenuForAdmins = () => {
  //   console.log('getMenuForAdmins');
  //   return {
  //     select: true, //change to select for a dropdown
  //     children: admins && admins.map((admin) => (
  //       <MenuItem key={admin.guid} value={admin.first_name+ " " +admin.last_name}>
  //         {admin.first_name} {admin.last_name}
  //       </MenuItem>
  //     )),
  //   };
  // }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'guid',
        header: 'ID',
        enableColumnOrdering: false,
        enableEditing: false, //disable editing on this column
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 20,
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 30,
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          ...getCommonEditTextFieldProps(cell),
          type: 'string',
        }),
      },
      {
        accessorKey: 'number_of_slots',
        header: 'Number of Slots',
        size: 30,
        muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
          ...getCommonEditTextFieldProps(cell),
          type: 'number',
        }),
      },
      {
        accessorKey: 'sales_amount',
        header: 'Sales Amount',
        size: 40,
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
      },
      {
        accessorKey: 'has_coin_dispenser',
        header: 'Cash Dispenser',
        size: 30,
        Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'),
        muiTableBodyCellEditTextFieldProps: {
          select: true,
          children: [
            <MenuItem key="cash-dispenser-no" value={false}>No</MenuItem>,
            <MenuItem key="cash-dispenser-yes" value={true}>Yes</MenuItem>,
          ],
        },
      },
      {
        accessorKey: 'single_item_vm',
        header: 'Single Item VM',
        size: 30,
        Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'),
        muiTableBodyCellEditTextFieldProps: {
          select: true,
          children: [
            <MenuItem key="single-item-no" value={false}>No</MenuItem>,
            <MenuItem key="single-item-yes" value={true}>Yes</MenuItem>,
          ],
        },
      },
      {
        accessorKey: 'single_item_vm_count',
        header: 'Item Types',
        size: 30,
        Cell: ({ row }) => (row.original.single_item_vm ? row.original.single_item_vm_count : 'Any'),
        muiTableBodyCellEditTextFieldProps: {
          select: true,
          children: [1, 2, 3, 4].map((value) => (
            <MenuItem key={`single-item-count-${value}`} value={value}>{value}</MenuItem>
          )),
        },
      },
      {
        accessorKey: 'admin_name',
        header: 'Admin',
        muiTableBodyCellEditTextFieldProps: ({cell})=>({
          select: true, //change to select for a dropdown
          onChange: (event) => {setAdmin(event.target.value)},
          // defaultValue:cell.getValue(),
          // displayEmpty: true,
          // renderValue: cell.getValue(),
          children: admins && admins.map((admin) => (
            <MenuItem value={admin.guid}>
              {admin.first_name} {admin.last_name}
            </MenuItem>
          )),
        }),
      },
      {
        accessorKey: 'created_at',
        header: 'Created At',
        size: 40,
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        size: 40,
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
      },
    ],
    [getCommonEditTextFieldProps, admins],
  );

  return (
    <>
      {loading? <TableRowsLoader /> : <MaterialReactTable
        displayColumnDefOptions={{
          'mrt-row-actions': {
            muiTableHeadCellProps: {
              align: 'center',
            },
            size: 120,
          },
        }}
        columns={columns}
        data={tableData}
        editingMode="modal" //default
        enableColumnOrdering
        enableColumnActions={false}
        enableEditing
        onEditingRowSave={handleSaveRowEdits}
        onEditingRowCancel={handleCancelRowEdits}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <Tooltip arrow placement="left" title="MQTT Info">
              <IconButton
                color="info"
                onClick={() => handleOpenMqttCredentials(row)}
                disabled={fetchingMqttCredentialsForVm === row?.original?.guid}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="left" title="Edit">
              <IconButton onClick={() => table.setEditingRow(row)}>
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip arrow placement="right" title="Delete">
              <IconButton color="error" onClick={() => handleDeleteRow(row)}>
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        renderTopToolbarCustomActions={() => (
          <Button
            color="secondary"
            onClick={() => setCreateModalOpen(true)}
            variant="contained"
          >
            Create New Vending Machine
          </Button>
        )}
      />}
      <CreateNewAccountModal
        columns={columns}
        admin={admin}
        admins={admins}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateNewRow}
        onHandleChange={handleChange}
      />
      <GeneratedMqttCredentialsModal
        open={mqttCredentialsDialogOpen}
        title={credentialsDialogTitle}
        description={credentialsDialogDescription}
        credentials={generatedMqttCredentials}
        onClose={() => setMqttCredentialsDialogOpen(false)}
        onCopy={copyToClipboard}
      />
      <Snackbar
        open={Boolean(copyMessage)}
        autoHideDuration={1800}
        onClose={handleCloseCopyMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={handleCloseCopyMessage}>
          {copyMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

//example of creating a mui dialog modal for creating new rows
export const CreateNewAccountModal = ({ open, columns, admin, admins, onClose, onSubmit, onHandleChange }) => {
  const [values, setValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {}),
  );

  const handleSubmit = () => {
    //put your validation logic here
    onSubmit(values);
    onClose();
  };

  return (
    <Dialog open={open}>
      <DialogTitle textAlign="center">Create New Vending Machine</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px' },
              gap: '1.5rem',
              my:3,
            }}
          >
            {columns.map((column) => (
              (column.accessorKey === 'number_of_slots' || column.accessorKey === 'name') && <TextField
                key={column.accessorKey}
                label={column.header}
                name={column.accessorKey}
                onChange={(e) =>
                  setValues({ ...values, [e.target.name]: e.target.value })
                }
              />
            ))}
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(values.has_coin_dispenser)}
                onChange={(event) =>
                  setValues({ ...values, has_coin_dispenser: event.target.checked })
                }
              />
            }
            label="Has Cash Dispenser"
          />
          <InputLabel id="single-item-vm-label">Single Item Vending Machine</InputLabel>
          <Select
            fullWidth
            labelId="single-item-vm-label"
            value={values.single_item_vm === true ? 'yes' : 'no'}
            label="Single Item Vending Machine"
            onChange={(event) => {
              const isSingleItem = event.target.value === 'yes';
              setValues({
                ...values,
                single_item_vm: isSingleItem,
                single_item_vm_count: isSingleItem ? (values.single_item_vm_count || 1) : 0,
              });
            }}
          >
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
          </Select>
          {values.single_item_vm === true && (
            <>
              <InputLabel id="single-item-count-label">Number of Item Types</InputLabel>
              <Select
                fullWidth
                labelId="single-item-count-label"
                value={values.single_item_vm_count || 1}
                label="Number of Item Types"
                onChange={(event) => setValues({ ...values, single_item_vm_count: event.target.value })}
              >
                {[1, 2, 3, 4].map((value) => (
                  <MenuItem key={`create-single-item-count-${value}`} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </>
          )}
          <InputLabel id="demo-simple-select-label">User Type</InputLabel>
            <Select 
                fullWidth
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={admin}
                label="User Type"
                name="user_type"
                onChange={onHandleChange}
            >
              {admins && admins.map((admin)=>{
                return <MenuItem value={admin.guid}>{admin.first_name} {admin.last_name}</MenuItem>
              })}
                
            </Select>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ p: '1.25rem' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="secondary" onClick={handleSubmit} variant="contained">
          Create New Vending Machine
        </Button>
      </DialogActions>
    </Dialog>
  );
};


const GeneratedMqttCredentialsModal = ({ open, title, description, credentials, onClose, onCopy }) => {
  if (!credentials) {
    return null;
  }

  const fields = [
    { key: 'username', label: 'MQTT Username', value: credentials.username || '' },
    { key: 'password', label: 'MQTT Password', value: credentials.password || '' },
    { key: 'broker_host', label: 'Broker Host', value: credentials.broker_host || '' },
    { key: 'broker_port', label: 'Broker Port', value: credentials.broker_port || '' },
    { key: 'slot_topic_example', label: 'Slot Topic (Device 1)', value: credentials.slot_topic_example || '' },
    { key: 'slot_topic_template', label: 'Slot Topic Template', value: credentials.slot_topic_template || '' },
  ];

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle textAlign="center">{title || "MQTT Credentials"}</DialogTitle>
      <DialogContent>
        <Stack sx={{ width: '100%', minWidth: { xs: '300px', sm: '480px' }, gap: '1rem', my: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {description || 'Copy these once and configure the Pi/ESP devices for this VM.'}
          </Typography>
          {fields.map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              value={field.value}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" size="small" onClick={() => onCopy(field.label, field.value)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '1.25rem' }}>
        <Button onClick={onClose} variant="contained" color="secondary">Done</Button>
      </DialogActions>
    </Dialog>
  );
};


const validateRequired = (value) => !!value.length;
const validateEmail = (email) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
// const validateAge = (age) => age >= 18 && age <= 50;

export default VendingMachine;
